import { TermsSignaturePendingError, isAuthFailureResponse, withTokenRetry } from "api/with-token-retry"
import { log } from "helpers/logger"

const CANCEL_REQUEST_TIMEOUT_MILLISECONDS = 10000

// The bespoke chunk this transport emits for a `thread_created` event; consumers that don't tap it pass it through untouched.
export const THREAD_METADATA_CHUNK_TYPE = "thread-metadata"

// Same pass-through mechanism, carrying the assistant row a FAILED turn persisted so the chatbox can
// advance its continue guard; without it the next send re-parents on a head the server has moved past.
export const TURN_FAILURE_METADATA_CHUNK_TYPE = "turn-failure-metadata"

// Same pass-through mechanism, signaling that the server is reconnecting after a provider stall. Not a failure.
export const STREAM_RETRYING_CHUNK_TYPE = "stream-retrying"

function parseFrameText(frameText) {
  const frame = { id: null, event: null, data: null }

  for (const line of frameText.split("\n")) {
    if (!line || line.startsWith(":")) continue
    const colon = line.indexOf(":")
    if (colon < 0) continue
    const field = line.slice(0, colon)
    const rawValue = line.slice(colon + 1)
    const value = rawValue.startsWith(" ") ? rawValue.slice(1) : rawValue
    if (field === "id") frame.id = value
    else if (field === "event") frame.event = value
    else if (field === "data") {
      frame.data = frame.data === null ? value : `${frame.data}\n${value}`
    }
  }

  return frame
}

export async function* parseSseFrames(readableStream) {
  const reader = readableStream.getReader()
  const decoder = new TextDecoder()
  let buffer = ""

  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      let boundary = buffer.indexOf("\n\n")
      while (boundary !== -1) {
        const frame = parseFrameText(buffer.slice(0, boundary))
        buffer = buffer.slice(boundary + 2)
        if (frame.data !== null) yield frame
        boundary = buffer.indexOf("\n\n")
      }
    }
    if (buffer.length > 0) {
      const frame = parseFrameText(buffer)
      if (frame.data !== null) yield frame
    }
  } finally {
    reader.releaseLock?.()
  }
}

function parseDataPayload(frame) {
  try {
    return JSON.parse(frame.data)
  } catch {
    return null
  }
}

const TEXT_PART_ID = "assistant-text"

const TERMINAL_EVENT_TYPES = new Set(["final_message", "error", "cancelled"])

function toolCallIdFor(event) {
  return event.payload?.tool_call_id ?? event.id
}

function buildToolFinishedChunks(event) {
  const toolCallId = toolCallIdFor(event)
  const { error_type: errorType, trace_id: traceId, result, result_truncated: resultTruncated } = event.payload ?? {}
  if (!errorType) {
    const output = { status: "ok" }
    if (result != null) output.result = result
    if (resultTruncated != null) output.resultTruncated = resultTruncated
    return [{ type: "tool-output-available", toolCallId, output }]
  }
  const errorText = traceId ? `${errorType} (trace ${traceId})` : errorType
  return [{ type: "tool-output-error", toolCallId, errorText }]
}

// op-llm-tools keys text accumulation on the (message_event_id, index) pair, not message_event_id
// alone (op-llm-tools chat/progress.py; ADR 0010). A missing or empty id means the runner never
// stamped one (its documented "" fallback, or the buffered path) — collapse to the single fixed part.
function textPartIdFor(event) {
  const messageEventId = event.payload?.message_event_id
  if (!messageEventId) return TEXT_PART_ID
  return `${messageEventId}#${event.payload?.index ?? 0}`
}

export function createTextStreamState() {
  return { activeTextPartId: null }
}

// Interstitial messages follow a text→tool pattern, so an open text part must be closed on an
// intra-turn boundary — otherwise it stays open across the tool calls that follow and the streaming
// runtime drops it (PROD-449). Returns the closing chunk (and clears the state) when a part is open.
function flushActiveText(textStreamState) {
  if (textStreamState.activeTextPartId == null) return []
  const chunk = { type: "text-end", id: textStreamState.activeTextPartId }
  textStreamState.activeTextPartId = null
  return [chunk]
}

// Boundaries that must close a preceding interstitial text part. Deliberately excludes turn_finished:
// on the wire it fires after the final answer's deltas with that part still open, and final_message
// already closes it — flushing here would leave final_message to re-emit a duplicate full-text part.
const TEXT_FLUSH_BOUNDARY_EVENT_TYPES = new Set(["tool_started", "tool_finished", "error", "cancelled"])

// Shared with chat-api-errors' session-terminated bucket, which imports it from here: the same
// sentence in two files drifts apart.
export const SESSION_ENDED_MESSAGE = "This conversation has ended. Start a new chat to keep going."

// Copy lives here, not in chat-api-errors: MUI's adapter rebuilds the rejection from `errorText` alone.
// Null-prototype: `error_type` is a server string, and a plain object would resolve "constructor" or
// "toString" to an inherited function, which is truthy and would become the errorText.
export const TURN_FAILURE_MESSAGES = Object.assign(Object.create(null), {
  ManagedAgentTurnCeilingError: "Kelly ran out of time on this step. Send another message to keep going.",
  ManagedAgentStreamStalledError: "The assistant stopped responding partway through. Please try again.",
  ManagedAgentStreamTimeoutError: "The assistant stopped responding partway through. Please try again.",
  ManagedAgentHostedToolTimeoutError: "A tool Kelly was running timed out. Please try again.",
  ManagedAgentUpstreamFaultError: "The AI provider had a problem on their side. Please send your message again.",
  ManagedAgentOverloadedError: "The assistant's model provider is overloaded. Please start a new chat.",
  ManagedAgentAPIError: "The assistant had trouble reaching its model. Please try again.",
  ManagedAgentNetworkError: "The assistant had trouble reaching its model. Please try again.",
  ManagedAgentStreamOverloadedError: "The assistant's model provider is overloaded. Please start a new chat.",
  ManagedAgentRetriesExhaustedError: "The assistant's model provider is overloaded. Please start a new chat.",
  ManagedAgentFallbackOverloadError: "The assistant's model provider is overloaded. Please start a new chat.",
  ManagedAgentRateLimitError: "The assistant is busy right now. Please try again in a moment.",
  ManagedAgentSessionTerminatedError: SESSION_ENDED_MESSAGE,
  ManagedAgentError: "The assistant couldn't finish this response. Please send your message again.",
})

// An unmapped error_type used to reach the reader as a bare class name, which says nothing to them and
// outlives the turn it describes when the view rolls back (APP-3527). The class and trace go to Sentry instead.
const UNEXPECTED_TURN_FAILURE_MESSAGE = "Kelly hit an unexpected problem on this turn. Please try again."

// The ceiling copy invites the reader to keep going, which only holds when the server kept the turn. Without a
// persisted row the view rolls back to New chat and the next send opens a different thread (APP-3527).
const CEILING_DISCARDED_MESSAGE = "Kelly ran out of time on this step. Please try again."

function buildTextDeltaChunks(event, textStreamState) {
  const textPartId = textPartIdFor(event)
  const chunks = []
  if (textStreamState.activeTextPartId !== textPartId) {
    if (textStreamState.activeTextPartId != null) {
      chunks.push({ type: "text-end", id: textStreamState.activeTextPartId })
    }
    textStreamState.activeTextPartId = textPartId
    chunks.push({ type: "text-start", id: textPartId })
  }
  chunks.push({ type: "text-delta", id: textPartId, delta: event.payload?.delta ?? "" })
  return chunks
}

// snake_case wire keys renamed to their client camelCase form for the open (unknown-kind) pass-through.
const WIRE_ARTIFACT_KEY_RENAMES = {
  artifact_id: "id",
  explain_steps: "explainSteps",
  table_artifact_id: "tableArtifactId",
}

// Artifacts are an OPEN set (ADR 0007): unknown kinds pass through generically so a fallback renderer
// can handle them, rather than being dropped. Known kinds keep their explicit projection.
function mapOpenArtifact(artifact) {
  const mapped = {}
  for (const [key, value] of Object.entries(artifact)) {
    mapped[WIRE_ARTIFACT_KEY_RENAMES[key] ?? key] = value
  }
  return mapped
}

function mapFinalMessageArtifact(artifact) {
  if (artifact == null || typeof artifact !== "object") return null
  const { artifact_id: id, type } = artifact
  if (typeof id !== "string" || typeof type !== "string") return null
  switch (type) {
    case "table": {
      const { records, columns, explain_steps: explainSteps } = artifact
      return { id, type, records, columns, explainSteps }
    }
    case "plot": {
      const { figure, table_artifact_id: tableArtifactId } = artifact
      return { id, type, figure, tableArtifactId }
    }
    case "text": {
      const { text } = artifact
      return { id, type, text }
    }
    default:
      return mapOpenArtifact(artifact)
  }
}

function buildFinalMessageChunks(event, textStreamState) {
  const rawArtifacts = event.payload?.assistant_message?.artifacts
  const finishChunk = {
    type: "finish",
    messageMetadata: {
      threadId: event.thread_id,
      messageId: event.payload?.assistant_message?.message_id,
      text: event.payload?.assistant_message?.text,
      artifacts: (Array.isArray(rawArtifacts) ? rawArtifacts : []).map(mapFinalMessageArtifact).filter(Boolean),
    },
  }
  if (textStreamState.activeTextPartId != null) {
    return [{ type: "text-end", id: textStreamState.activeTextPartId }, finishChunk]
  }
  const text = event.payload?.assistant_message?.text ?? ""
  return [
    { type: "text-start", id: TEXT_PART_ID },
    { type: "text-delta", id: TEXT_PART_ID, delta: text },
    { type: "text-end", id: TEXT_PART_ID },
    finishChunk,
  ]
}

export function mapEventToChunks(event, textStreamState = createTextStreamState()) {
  // text_delta and final_message manage the active text part themselves; a tool/error/cancelled
  // boundary must close any open interstitial text part first (PROD-449), so its chunks follow the flush.
  switch (event.type) {
    case "text_delta":
      return buildTextDeltaChunks(event, textStreamState)
    case "final_message":
      return buildFinalMessageChunks(event, textStreamState)
    default: {
      const eventChunks = buildNonTextEventChunks(event)
      if (!TEXT_FLUSH_BOUNDARY_EVENT_TYPES.has(event.type)) return eventChunks
      return [...flushActiveText(textStreamState), ...eventChunks]
    }
  }
}

function buildNonTextEventChunks(event) {
  switch (event.type) {
    case "tool_started":
      return [
        { type: "tool-input-start", toolCallId: toolCallIdFor(event), toolName: event.tool_name },
        { type: "tool-input-available", toolCallId: toolCallIdFor(event), toolName: event.tool_name, input: {} },
      ]
    case "tool_finished":
      return buildToolFinishedChunks(event)
    case "error": {
      const { error_type: errorType, trace_id: traceId, assistant_message_id: assistantMessageId } = event.payload ?? {}
      const chunks = []
      const persistedTurn = typeof assistantMessageId === "string" && assistantMessageId.length > 0
      if (persistedTurn) {
        chunks.push({ type: TURN_FAILURE_METADATA_CHUNK_TYPE, assistantMessageId, threadId: event.thread_id })
      }
      // The reader-facing copy never carries the class name or trace, so keep both for Sentry — otherwise a
      // support report has nothing to correlate against.
      log(new Error(`${errorType ?? "unknown"} (trace ${traceId ?? "none"})`))
      const errorText = !persistedTurn && errorType === "ManagedAgentTurnCeilingError"
        ? CEILING_DISCARDED_MESSAGE
        : (TURN_FAILURE_MESSAGES[errorType] ?? UNEXPECTED_TURN_FAILURE_MESSAGE)
      // The server's failure class rides alongside the rendered copy. `errorText` is a sentence written for
      // the reader and never carries the class, so a consumer that needs to GROUP failures — Kelly's
      // reliability number — cannot recover it from there. Named `errorType` after the server field: it is the
      // SERVER's vocabulary, and a consumer that wants the app's taxonomy has to map it (chat-api-errors'
      // chatApiErrorKindForServerType) rather than assume.
      chunks.push({ type: "error", errorText, ...(typeof errorType === "string" && errorType.length > 0 && { errorType }) })
      return chunks
    }
    case "cancelled":
      return [{ type: "finish", finishReason: "abort", messageMetadata: { threadId: event.thread_id } }]
    case "thread_created":
      // Early (pre-turn) thread id so the chatbox can adopt the new thread mid-stream; the drawer's runtime ignores this chunk type.
      return [{ type: THREAD_METADATA_CHUNK_TYPE, threadId: event.thread_id }]
    case "stream_retrying":
      return [{ type: STREAM_RETRYING_CHUNK_TYPE }]
    case "turn_started":
    case "turn_finished":
    default:
      return []
  }
}

async function requestWithTokenRetry({ url, method, body, extraHeaders, abortSignal, account, userId, getNeedSignTerms }) {
  async function doFetch(token) {
    return fetch(url, {
      method,
      credentials: "same-origin",
      headers: {
        Accept: "text/event-stream",
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
        "X-OmegaPoint-Code": token,
        ...extraHeaders,
      },
      ...(body !== undefined ? { body } : {}),
      signal: abortSignal,
    })
  }

  return withTokenRetry({
    account,
    userId,
    getNeedSignTerms,
    attempt: doFetch,
    isAuthFailure: isAuthFailureResponse,
    onRefreshBlocked: () => { throw new TermsSignaturePendingError() },
  })
}

function parseJsonOrNull(text) {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

export class StreamResumeError extends Error {
  constructor(kind, status) {
    super(`stream_resume_${kind}`)
    this.name = "StreamResumeError"
    this.kind = kind
    this.status = status
  }
}

// The socket ended with no final_message, error or cancelled frame. Named so the consumer can tell a turn that
// died in flight from a send that never opened, without inspecting a message string (APP-3527).
export class TruncatedStreamError extends Error {
  constructor() {
    super("stream ended before a terminal event")
    this.name = "TruncatedStreamError"
  }
}

function isExpectedCancelStatus(status) {
  return status === 404 || status === 410
}

// Matches the bound pi-server applies before forwarding a parsed upstream error.
const MAX_UPSTREAM_ERROR_CHARS = 16 * 1024

// Speaks the same `code`/`error` vocabulary as the blocking-route error classes, so a
// caller can tell a budget cap from a real outage without learning a second shape.
export class UpstreamResponseError extends Error {
  constructor(status, text) {
    super(`upstream ${status}: ${text.slice(0, 200)}`)
    this.name = "UpstreamResponseError"
    this.code = status
    const body = text.length <= MAX_UPSTREAM_ERROR_CHARS ? parseJsonOrNull(text) : null
    this.error = body?.upstreamError ?? null
  }
}

async function upstreamResponseError(response) {
  const text = await response.text().catch(() => "")
  return new UpstreamResponseError(response.status, text)
}

async function pipeSseResponseToStream(response, controller, { onFrame, onEvent, tolerateNonTerminalEnd } = {}) {
  if (!response.ok || !response.body) {
    throw await upstreamResponseError(response)
  }
  const textStreamState = createTextStreamState()
  let sawTerminalEvent = false
  for await (const frame of parseSseFrames(response.body)) {
    onFrame?.(frame)
    const event = parseDataPayload(frame)
    if (!event) continue
    onEvent?.(event)
    if (TERMINAL_EVENT_TYPES.has(event.type)) sawTerminalEvent = true
    for (const chunk of mapEventToChunks(event, textStreamState)) {
      controller.enqueue(chunk)
    }
  }
  if (sawTerminalEvent) return
  // A resumed replay can end without its terminal event (the SSE dropped mid-replay). Erroring discards the
  // whole queue — controller.error() resets it — so the answer that DID assemble is lost; finalizing keeps it
  // (APP-3376). Opt-in per call: on a fresh send a non-terminal end IS truncation and must not commit.
  if (!tolerateNonTerminalEnd) throw new TruncatedStreamError()
  // Close the open text part so the assembled text is a complete part. No synthetic finish: the stream ending
  // is the terminal signal, and inventing finish metadata would claim ids/a clean finish the server never sent.
  for (const chunk of flushActiveText(textStreamState)) {
    controller.enqueue(chunk)
  }
}

export class ChatTransport {
  constructor({ accountAlias, account, userId, getNeedSignTerms } = {}) {
    this.accountAlias = accountAlias
    this.account = account
    this.userId = userId
    this._lastEventIdByChatId = new Map()
    this._streamIdByChatId = new Map()
    this._newThreadStreamId = null
    this.getNeedSignTerms = getNeedSignTerms
  }

  sendMessages({ chatId, body, abortSignal } = {}) {
    const url = chatId
      ? `/api/${this.accountAlias}/ai/chat/threads/${chatId}/stream`
      : `/api/${this.accountAlias}/ai/chat/threads/stream`

    return new ReadableStream({
      start: (controller) => this._streamRequest(controller, {
        url,
        method: "POST",
        body: JSON.stringify(body ?? {}),
        abortSignal,
        chatId,
        onFrame: (frame) => {
          if (chatId && frame.id != null) this._lastEventIdByChatId.set(chatId, frame.id)
        },
        onEvent: (event) => this._trackStreamId(chatId, event),
      }),
    })
  }

  /**
   * Reattach to a thread's in-flight stream. `tolerateNonTerminalEnd` finalizes an assembled partial when the
   * replay ends without a terminal event instead of discarding it (APP-3376) — opt-in, because a consumer that
   * tracks per-thread pending state off the finish metadata needs the rejection to clear it.
   */
  async reconnectToStream({ chatId, abortSignal, tolerateNonTerminalEnd, fromStart } = {}) {
    let streamId
    try {
      streamId = await this._resolveStreamId(chatId, abortSignal)
    } catch (error) {
      if (chatId != null && error != null && typeof error === "object") error.chatId = chatId
      this._streamIdByChatId.delete(chatId)
      this._lastEventIdByChatId.delete(chatId)
      throw error
    }
    if (streamId == null) return null

    const url = `/api/${this.accountAlias}/ai/chat/streams/${streamId}/resume`
    // fromStart: omit Last-Event-ID to replay the whole stream; see reattachActiveStream for the full rationale.
    const lastEventId = fromStart ? undefined : this._lastEventIdByChatId.get(chatId)

    return new ReadableStream({
      start: (controller) => this._streamRequest(controller, {
        url,
        method: "GET",
        extraHeaders: lastEventId != null ? { "Last-Event-ID": lastEventId } : undefined,
        abortSignal,
        chatId,
        resumeChatId: chatId,
        tolerateNonTerminalEnd,
        checkResponse: (response) => {
          if (response.status === 404) throw new StreamResumeError("not_found", response.status)
          if (response.status === 410) throw new StreamResumeError("expired", response.status)
        },
        onFrame: (frame) => {
          if (frame.id != null) this._lastEventIdByChatId.set(chatId, frame.id)
        },
        onEvent: (event) => this._trackStreamId(chatId, event),
      }),
    })
  }

  _trackStreamId(chatId, event) {
    if (!chatId) {
      if (event?.stream_id != null) this._newThreadStreamId = event.stream_id
      if (TERMINAL_EVENT_TYPES.has(event?.type)) this._newThreadStreamId = null
      return
    }
    if (event?.stream_id != null) this._streamIdByChatId.set(chatId, event.stream_id)
    if (TERMINAL_EVENT_TYPES.has(event?.type)) {
      this._streamIdByChatId.delete(chatId)
      this._lastEventIdByChatId.delete(chatId)
    }
  }

  async cancelStream(chatId) {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), CANCEL_REQUEST_TIMEOUT_MILLISECONDS)
    try {
      if (!chatId) {
        const newThreadStreamId = this._newThreadStreamId
        if (newThreadStreamId == null) return
        this._newThreadStreamId = null
        await this._deleteStream(newThreadStreamId, controller.signal)
        return
      }
      let streamId
      try {
        streamId = await this._resolveStreamId(chatId, controller.signal)
      } catch (error) {
        log(error)
        return
      }
      if (streamId == null) return
      this._streamIdByChatId.delete(chatId)
      this._lastEventIdByChatId.delete(chatId)
      await this._deleteStream(streamId, controller.signal)
    } finally {
      clearTimeout(timeoutId)
    }
  }

  async _deleteStream(streamId, abortSignal) {
    try {
      const response = await requestWithTokenRetry({
        url: `/api/${this.accountAlias}/ai/chat/streams/${streamId}`,
        method: "DELETE",
        abortSignal,
        account: this.account,
        userId: this.userId,
        getNeedSignTerms: this.getNeedSignTerms,
      })
      if (!response.ok && !isExpectedCancelStatus(response.status)) {
        log(await upstreamResponseError(response))
      }
    } catch (error) {
      log(error)
    }
  }

  async _resolveStreamId(chatId, abortSignal) {
    const trackedStreamId = this._streamIdByChatId.get(chatId)
    if (trackedStreamId != null) return trackedStreamId

    const url = `/api/${this.accountAlias}/ai/chat/threads/${chatId}/active-stream`
    const response = await requestWithTokenRetry({
      url,
      method: "GET",
      abortSignal,
      account: this.account,
      userId: this.userId,
      getNeedSignTerms: this.getNeedSignTerms,
    })
    if (response.status === 204) return null
    if (!response.ok) {
      throw await upstreamResponseError(response)
    }

    const { streamId, lastEventId } = await response.json()
    this._streamIdByChatId.set(chatId, streamId)
    if (lastEventId != null && !this._lastEventIdByChatId.has(chatId)) {
      this._lastEventIdByChatId.set(chatId, lastEventId)
    }
    return streamId
  }

  async _streamRequest(controller, { url, method, body, extraHeaders, abortSignal, chatId, resumeChatId, tolerateNonTerminalEnd, checkResponse, onFrame, onEvent }) {
    try {
      const response = await requestWithTokenRetry({
        url,
        method,
        body,
        extraHeaders,
        abortSignal,
        account: this.account,
        userId: this.userId,
        getNeedSignTerms: this.getNeedSignTerms,
      })
      checkResponse?.(response)
      await pipeSseResponseToStream(response, controller, { onFrame, onEvent, tolerateNonTerminalEnd })
      controller.close()
    } catch (error) {
      if (error != null && typeof error === "object") error.chatId = chatId ?? null
      if (resumeChatId != null) {
        this._streamIdByChatId.delete(resumeChatId)
        this._lastEventIdByChatId.delete(resumeChatId)
      }
      controller.error(error)
    }
  }
}
